import { useState } from 'react'
import { SupportThreadStatus } from '@/gen/cortado/v1/support_pb'
import type { SupportThread } from '@/gen/cortado/v1/support_pb'
import { useCurrentOrganization } from '@/stores/organization.store'
import { useSupportThreads, useSupportArchive } from '@/stores/support.store'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Plus, MessageCircle, Archive, Loader2 } from 'lucide-react'
import { StatusBadge } from './StatusBadge'
import { formatTimeAgo } from './utils'
import { cn } from '@/lib/utils'

interface ThreadListProps {
  open: boolean
  onSelectThread: (threadId: string) => void
  onNewThread: () => void
}

const STATUS_FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'New', value: String(SupportThreadStatus.NEW) },
  { label: 'In Progress', value: String(SupportThreadStatus.IN_PROGRESS) },
  { label: 'Resolved', value: String(SupportThreadStatus.RESOLVED) },
  { label: 'Archived', value: 'archived' },
] as const

export function ThreadList({ open, onSelectThread, onNewThread }: ThreadListProps) {
  const [statusFilter, setStatusFilter] = useState('all')
  const { currentOrganization } = useCurrentOrganization()
  const orgId = currentOrganization?.id || ''

  const { threads, isLoading, hasLoaded } = useSupportThreads(orgId, statusFilter, open)
  const { archive, isPending: isArchivePending, archivingThreadId } = useSupportArchive()

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between px-4 pb-3">
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList className="h-7">
            {STATUS_FILTERS.map((f) => (
              <TabsTrigger key={f.value} value={f.value} className="text-xs px-2 py-1">
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Button size="sm" variant="outline" onClick={onNewThread} className="h-7 gap-1">
          <Plus className="h-3.5 w-3.5" />
          New
        </Button>
      </div>

      <ScrollArea className="min-h-0 flex-1">
        {isLoading && !hasLoaded ? (
          <div className="flex flex-col gap-1">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-3 border-b border-border/50">
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : threads.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <MessageCircle className="h-8 w-8 opacity-40" />
            <p>No threads yet</p>
            <Button size="sm" variant="outline" onClick={onNewThread}>
              Start a conversation
            </Button>
          </div>
        ) : (
          <div className="flex flex-col">
            {threads.map((thread) => (
              <ThreadRow
                key={thread.id}
                thread={thread}
                onClick={() => onSelectThread(thread.id)}
                onArchive={() => archive(thread.id)}
                isArchiving={isArchivePending && archivingThreadId === thread.id}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  )
}

function ThreadRow({ thread, onClick, onArchive, isArchiving }: { thread: SupportThread; onClick: () => void; onArchive: () => void; isArchiving: boolean }) {
  return (
    <div className="group relative flex items-start border-b border-border/50 transition-colors hover:bg-muted/50">
      <button
        type="button"
        onClick={onClick}
        className="min-w-0 flex-1 px-4 py-3 text-left outline-none focus-visible:bg-muted/50"
      >
        <div className="flex items-center gap-2">
          {thread.hasUnread && (
            <span className="h-2 w-2 rounded-full bg-destructive flex-shrink-0" aria-label="Unread" />
          )}
          <span className={cn("truncate text-sm", thread.hasUnread ? "font-semibold" : "font-medium")}>
            {thread.subject || 'New thread'}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <StatusBadge status={thread.status} />
          {thread.updatedAt && (
            <span className="text-xs text-muted-foreground">
              {formatTimeAgo(thread.updatedAt)}
            </span>
          )}
        </div>
      </button>
      {!thread.archivedAt && (
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "mr-3 mt-3 h-7 w-7 shrink-0 transition-opacity",
            isArchiving ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
          )}
          disabled={isArchiving}
          onClick={onArchive}
          aria-label="Archive thread"
          title="Archive thread"
        >
          {isArchiving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />}
        </Button>
      )}
    </div>
  )
}
